// Generates the Starter Build delivery package content from a build's form inputs.

const featureList = (build) =>
  (build.core_features || "")
    .split(/[\n,]+/)
    .map((f) => f.trim())
    .filter(Boolean);

const bullet = (arr) => arr.map((x) => `• ${x}`).join("\n");

const slug = (s) => (s || "app").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");

export const generateBuilderPrompt = (build) => {
  const features = featureList(build);
  const fb = features.length ? bullet(features) : "• Core feature set defined during discovery";
  const platform = build.target_platform || "your AI builder";
  return `Build a production-ready web application called "${build.app_name}".

INDUSTRY: ${build.industry || "General"}

OVERVIEW
${build.description || "A modern, premium web application."}

CORE FEATURES
${fb}

USER ROLES
• Guest — browse public content
• Registered User — access core features and save data
• Admin — manage users, content, and analytics

REQUIRED SCREENS
• Landing / Home
• Sign Up & Login
• Onboarding
• Dashboard
${features.slice(0, 4).map((f) => `• ${f} screen`).join("\n")}
• Settings & Profile
• Admin Dashboard

DATABASE STRUCTURE
• users (id, name, email, password_hash, role, created_at)
• sessions (id, user_id, token, expires_at)
${features.slice(0, 4).map((f) => `• ${slug(f)} (id, user_id, data, created_at)`).join("\n")}
• subscriptions (id, user_id, plan, status)

AUTHENTICATION FLOW
• Email + password (or Google OAuth) registration and login
• JWT / session-based protected routes
• Role-based access (user vs admin)
• Password reset via email

ADMIN DASHBOARD
• Manage users and roles
• Manage content / listings
• View key metrics and activity
• Basic analytics overview

UI DESIGN STYLE
Premium, Apple-inspired interface: clean white background, black typography, gold accent buttons, rounded cards, soft shadows, generous spacing, smooth animations, fully mobile responsive.

INSTRUCTIONS FOR ${platform.toUpperCase()}
Paste this prompt into ${platform} to scaffold the MVP. Build iteratively: auth first, then the dashboard, then each core feature. Ship a scalable, clean, production-ready MVP.`;
};

export const generateDocumentation = (build) => {
  const features = featureList(build);
  const fb = features.length ? bullet(features) : "• Defined during discovery";
  return `${build.app_name.toUpperCase()} — PROJECT DOCUMENTATION
Industry: ${build.industry || "General"}
====================================================

1. APP OVERVIEW
${build.description || "A modern, premium web application."}

2. FEATURE LIST
${fb}

3. USER FLOW
Sign up / Log in → Onboarding → Dashboard → Use core feature (${features[0] || "primary feature"}) → Save / manage items → Return via dashboard.

4. SCREEN DESCRIPTIONS
• Landing / Home — hero, value proposition, primary CTA
• Auth — sign up, login, password reset
• Onboarding — capture user preferences
• Dashboard — overview of user's activity and key actions
${features.slice(0, 5).map((f) => `• ${f} — dedicated screen to manage ${f.toLowerCase()}`).join("\n")}
• Settings & Profile — account management
• Admin Dashboard — manage users, content, and metrics

5. DATABASE STRUCTURE
• users (id, name, email, password_hash, role, created_at)
• sessions (id, user_id, token, expires_at)
${features.slice(0, 5).map((f) => `• ${slug(f)} (id, user_id, data, created_at)`).join("\n")}
• subscriptions (id, user_id, plan, status)

6. AUTHENTICATION REQUIREMENTS
Email/password or Google OAuth, JWT/session tokens, protected routes, role-based access, password reset via email.

7. ADMIN REQUIREMENTS
User management, content management, activity monitoring, and a basic analytics overview.

8. FUTURE FEATURE SUGGESTIONS
• Notifications and email digests
• In-app analytics for end users
• Team / multi-user workspaces
• AI-assisted automation for repetitive tasks
• Mobile app companion`;
};

export const generateBlueprint = (build) => {
  const features = featureList(build);
  const platform = build.target_platform || "React + FastAPI + MongoDB";
  return `${build.app_name.toUpperCase()} — DEVELOPMENT BLUEPRINT
Recommended platform: ${platform}
====================================================

1. FOLDER STRUCTURE
/frontend
  /src
    /components
    /pages
    /hooks
    /lib
/backend
  server.py (API routes under /api)
  models/
  seed_data.py

2. RECOMMENDED PAGES
• Home / Landing
• Auth (Login / Sign Up / Reset)
• Onboarding
• Dashboard
${features.slice(0, 5).map((f) => `• ${f}`).join("\n")}
• Settings & Profile
• Admin Dashboard

3. NAVIGATION FLOW
Top navigation (Home, Dashboard, ${features[0] || "Features"}, Settings) with an authenticated area and a persistent account menu. Admin routes gated by role.

4. COMPONENTS
• Layout / Navbar / Footer
• Cards, Modals, Forms, Tables
• Empty states and loading skeletons
• Toasts / notifications

5. USER JOURNEY
Discover (landing) → Register → Onboard → Act (core feature) → Retain (dashboard + settings) → Upgrade (subscription).

6. RECOMMENDED INTEGRATIONS
• Authentication (JWT or Google OAuth)
• Payments (Stripe) for subscriptions
• Email (transactional notifications)
• Object storage for file uploads (if needed)
• LLM API for any AI-assisted features

7. BUILD ORDER
1) Auth + user model
2) Dashboard shell + navigation
3) Core features one by one
4) Admin dashboard
5) Payments + polish + responsive QA`;
};

export const DELIVERABLES = [
  "Interactive Live Preview",
  "Complete AI Builder Prompt",
  "Page-by-Page Build Instructions",
  "Feature Documentation",
  "User Flow Documentation",
  "Database Structure",
  "Authentication Flow",
  "Admin Dashboard Requirements",
  "Navigation Map",
  "Responsive Layout Instructions",
];

export const downloadText = (filename, text) => {
  const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};
